import * as React from "react";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import type {
  OverlayDirectionStats,
  SimulationListFilter,
} from "../../../../../utils/cohortTradesResolve";

const FILTER_OPTIONS: Array<{ value: SimulationListFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "changed", label: "Changed trades" },
  { value: "unchanged", label: "Unchanged trades" },
];

function isSimulationListFilter(value: string): value is SimulationListFilter {
  return FILTER_OPTIONS.some((opt) => opt.value === value);
}

function formatMoney(value: number): string {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatPct(value: number | null): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(1)}%`;
}

function simulationFilterScopeLabel(filter: SimulationListFilter): string {
  switch (filter) {
    case "changed":
      return "Changed";
    case "unchanged":
      return "Unchanged";
    case "all":
    default:
      return "Changed (affected)";
  }
}

function OverlayMetricsLine(props: {
  scopeLabel: string;
  stats: OverlayDirectionStats;
  scopeTotal: number | null;
}): React.ReactElement {
  const { scopeLabel, stats, scopeTotal } = props;
  return (
    <div
      data-simulation-overlay-metrics=""
      className="mb-3 flex flex-wrap items-baseline gap-x-3.5 gap-y-1 font-[family-name:var(--font-primary)] text-sm font-normal leading-snug text-foreground"
    >
      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        <span className="text-muted-foreground dark:text-sidebar-muted-foreground">{scopeLabel}:</span>
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">{stats.count}</span>
        {scopeTotal != null && scopeTotal > 0 ? (
          <span className="text-muted-foreground dark:text-sidebar-muted-foreground">
            of {scopeTotal} tagged
          </span>
        ) : null}
        <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
          ·
        </span>
        <span className="text-[color:var(--tessera-pnl-positive)]">{stats.wins}W</span>
        <span className="text-[color:var(--brand-glacial-red)]">{stats.losses}L</span>
        <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
          ·
        </span>
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">WR</span>
        <span>{formatPct(stats.winRate)}</span>
        <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
          ·
        </span>
        <span
          className={cn(
            stats.netPnl >= 0
              ? "text-[color:var(--tessera-pnl-positive)]"
              : "text-[color:var(--brand-glacial-red)]"
          )}
        >
          PnL {formatMoney(stats.netPnl)}
        </span>
      </span>

      <span className="text-muted-foreground/35 dark:text-sidebar-muted-foreground/35" aria-hidden>
        |
      </span>

      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">Longs:</span>
        <span>{stats.longs.count}</span>
        {stats.longs.count > 0 ? (
          <>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="text-[color:var(--tessera-pnl-positive)]">{stats.longs.wins}W</span>
            <span className="text-[color:var(--brand-glacial-red)]">{stats.longs.losses}L</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="font-medium text-[color:var(--brand-tropical-surf)]">WR</span>
            <span>{formatPct(stats.longs.winRate)}</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span
              className={cn(
                stats.longs.netPnl >= 0
                  ? "text-[color:var(--tessera-pnl-positive)]"
                  : "text-[color:var(--brand-glacial-red)]"
              )}
            >
              PnL {formatMoney(stats.longs.netPnl)}
            </span>
          </>
        ) : null}
      </span>

      <span className="text-muted-foreground/35 dark:text-sidebar-muted-foreground/35" aria-hidden>
        |
      </span>

      <span className="inline-flex flex-wrap items-baseline gap-x-1">
        <span className="font-medium text-[color:var(--brand-tropical-surf)]">Shorts:</span>
        <span>{stats.shorts.count}</span>
        {stats.shorts.count > 0 ? (
          <>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="text-[color:var(--tessera-pnl-positive)]">{stats.shorts.wins}W</span>
            <span className="text-[color:var(--brand-glacial-red)]">{stats.shorts.losses}L</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span className="font-medium text-[color:var(--brand-tropical-surf)]">WR</span>
            <span>{formatPct(stats.shorts.winRate)}</span>
            <span className="text-muted-foreground/40 dark:text-sidebar-muted-foreground/40" aria-hidden>
              ·
            </span>
            <span
              className={cn(
                stats.shorts.netPnl >= 0
                  ? "text-[color:var(--tessera-pnl-positive)]"
                  : "text-[color:var(--brand-glacial-red)]"
              )}
            >
              PnL {formatMoney(stats.shorts.netPnl)}
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
}

export type SimulationOverlaySummaryProps = {
  listFilter: SimulationListFilter;
  onListFilterChange: (filter: SimulationListFilter) => void;
  directionStats?: OverlayDirectionStats | null;
  scopeTotal?: number | null;
};

/** Changed / unchanged filter tabs for Trade Review exit-placement simulation overlay. */
export function SimulationOverlaySummary(props: SimulationOverlaySummaryProps): React.ReactElement {
  const stats = props.directionStats;
  const scopeLabel = simulationFilterScopeLabel(props.listFilter);
  const scopeTotal =
    typeof props.scopeTotal === "number" && Number.isFinite(props.scopeTotal)
      ? props.scopeTotal
      : null;

  return (
    <div data-simulation-overlay-summary="" className="mb-4 w-full min-w-0">
      {stats && stats.count > 0 ? (
        <OverlayMetricsLine scopeLabel={scopeLabel} stats={stats} scopeTotal={scopeTotal} />
      ) : stats && stats.count === 0 ? (
        <p className="mb-3 font-[family-name:var(--font-primary)] text-sm text-muted-foreground dark:text-sidebar-muted-foreground">
          No trades in this overlay filter.
        </p>
      ) : null}

      <div className="w-full min-w-0 overflow-x-auto overflow-y-hidden">
        <Tabs
          data-trade-log-tabs=""
          data-simulation-overlay-filter-tabs=""
          value={props.listFilter}
          onValueChange={(next) => {
            if (isSimulationListFilter(next)) props.onListFilterChange(next);
          }}
          className="w-fit"
        >
          <TabsList className="min-w-max">
            {FILTER_OPTIONS.map((opt) => (
              <TabsTrigger
                key={opt.value}
                value={opt.value}
                className="font-[family-name:var(--font-primary)] text-sm font-normal normal-case tracking-normal"
              >
                {opt.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
    </div>
  );
}
